import { snapLine, timeLineX } from "../overlays/grid";

// Grid lines are built directly in device pixels
const IDENTITY_TRANSFORM = [1, 0];
const ENDPOINT_FLOATS = 4;
// The separator over each pane is bolder than the grid (CSS px)
const SEPARATOR_WIDTH_PX = 2;

/**
 * Grid of the oscillator panes in device px: vertical lines at the time
 * labels through the area of the panes, horizontal ones at the price labels
 * of each pane, and over each pane a bolder separator across the whole chart.
 */
const buildPaneGrid = ({
  panes,
  area,
  timeXs,
  paneWidth,
  chartWidth,
  pixelRatio,
  colors,
  floatsPerLine,
  isSnappedX,
}) => {
  const gridWidth = Math.max(1, Math.floor(pixelRatio));
  const separatorWidth = Math.max(1, Math.round(SEPARATOR_WIDTH_PX * pixelRatio));
  const lines = [];
  const { top } = area;
  const bottom = area.top + area.height;
  const addLine = (endpoints, color, width) => lines.push({ endpoints, color, width });

  timeXs.forEach((x) => {
    const deviceX = timeLineX(x, pixelRatio, gridWidth, isSnappedX);

    addLine([deviceX, top * pixelRatio, deviceX, bottom * pixelRatio], colors.grid, gridWidth);
  });
  panes.forEach((pane) => {
    pane.priceLabels.forEach(({ y }) => {
      const deviceY = snapLine(y, pixelRatio, gridWidth);

      addLine([0, deviceY, paneWidth * pixelRatio, deviceY], colors.grid, gridWidth);
    });
  });
  // Last, so the separators lie over the grid
  panes.forEach((pane) => {
    const separatorY = snapLine(pane.top, pixelRatio, separatorWidth);

    addLine([0, separatorY, chartWidth * pixelRatio, separatorY], colors.separator, separatorWidth);
  });

  const data = new Float32Array(lines.length * floatsPerLine);

  lines.forEach(({ endpoints, color, width }, index) => {
    const offset = index * floatsPerLine;

    data.set(endpoints, offset);
    data.set(color, offset + ENDPOINT_FLOATS);
    data[offset + floatsPerLine - 1] = width;
  });

  return data;
};

const deviceRect = ({ x, y, width, height }, pixelRatio) => {
  const top = Math.round(y * pixelRatio);

  return {
    x: Math.round(x * pixelRatio),
    y: top,
    width: Math.round(width * pixelRatio),
    height: Math.round((y + height) * pixelRatio) - top,
  };
};

/**
 * Draws the oscillator panes under the price pane, clipped to the area of
 * the panes (the ones scrolled away are cut): their background and grid,
 * then the indicator of each pane clipped to its visible part, on the pane's price scale.
 *
 * @param {object} params
 * @param {object} params.glLayer
 * @param {object} params.gridLines - line renderer of the pane grid
 * @param {object} params.indicators - createIndicators
 * @param {{ pane: object, indicatorPanes: object[], paneArea: object, priceAxisWidth: number }} params.layout
 * @param {Array<{ x: number }>} params.timeTicks
 * @param {object} params.timeScale
 * @param {number} params.pixelRatio
 * @param {boolean} params.isSnappedX - see drawGeometry
 * @param {{ background: number[], grid: number[], separator: number[] }} params.colors - RGBA 0..1
 */
export const drawIndicatorPanes = ({
  glLayer,
  gridLines,
  indicators,
  layout,
  timeTicks,
  timeScale,
  pixelRatio,
  isSnappedX,
  colors,
}) => {
  const { pane, indicatorPanes, paneArea, priceAxisWidth } = layout;

  if (!indicatorPanes.length || paneArea.height <= 0) return;

  const viewport = glLayer.viewport;
  const chartWidth = pane.width + priceAxisWidth;

  glLayer.clipTo(deviceRect({ x: 0, y: paneArea.top, width: chartWidth, height: paneArea.height }, pixelRatio));
  glLayer.fillClip(colors.background);
  gridLines.setInstances(
    buildPaneGrid({
      panes: indicatorPanes,
      area: paneArea,
      timeXs: timeTicks.map((tick) => tick.x),
      paneWidth: pane.width,
      chartWidth,
      pixelRatio,
      colors,
      floatsPerLine: gridLines.floatsPerInstance,
      isSnappedX,
    }),
  );
  gridLines.draw({ viewport, xTransform: IDENTITY_TRANSFORM, yTransform: IDENTITY_TRANSFORM });

  indicatorPanes.forEach((indicatorPane) => {
    const { top, visibleTop, visibleHeight } = indicatorPane;

    if (visibleHeight <= 0) return;

    glLayer.clipTo(deviceRect({ x: 0, y: visibleTop, width: pane.width, height: visibleHeight }, pixelRatio));
    indicators.draw(indicatorPane.id, {
      viewport,
      timeScale,
      priceScale: indicatorPane.priceScale,
      top,
      pixelRatio,
      isSnappedX,
    });
  });
};
